import type {
  ConfidenceBand,
  DiagnosisConfidenceComponents,
  DiagnosisContext,
  DiagnosisCoverage,
  DiagnosisCoverageDimension,
  DiagnosisEvidence,
} from './contracts';

const known = (value: { status: string }): boolean => value.status === 'known';
const ratio = (values: readonly boolean[]): number =>
  values.length === 0 ? 0 : values.filter(Boolean).length / values.length;

function dimension(
  name: DiagnosisCoverageDimension['dimension'],
  values: readonly boolean[],
  explanation: string,
): DiagnosisCoverageDimension {
  const coverage = ratio(values);
  return {
    dimension: name,
    state: coverage === 0 ? 'unknown' : coverage === 1 ? 'known' : 'partial',
    coverage,
    explanation,
  };
}

export function calculateCoverage(context: DiagnosisContext): DiagnosisCoverage {
  const operational = context.graph.nodes.filter(
    (node) => node.category !== 'documentation' && node.status !== 'paused',
  );
  const acquisition = operational.filter((node) => node.category === 'acquisition');
  const retention = operational.filter((node) => node.category === 'retention');
  const dimensions: DiagnosisCoverageDimension[] = [
    dimension(
      'structure',
      [context.graph.nodes.length > 0, operational.length > 0, context.graph.edges.length > 0],
      'Graph nodes, operational stages and connections available.',
    ),
    dimension(
      'traffic',
      acquisition.length
        ? acquisition.flatMap((node) => [known(node.metrics.entries), known(node.metrics.exits)])
        : [],
      'Acquisition volume inputs available.',
    ),
    dimension(
      'conversion',
      operational.map((node) => known(node.metrics.conversion)),
      'Stage conversion values available.',
    ),
    dimension(
      'financial',
      [
        known(context.funnelMetrics.investment),
        known(context.funnelMetrics.revenue) || known(context.funnelMetrics.projectedRevenue),
        known(context.funnelMetrics.cac),
        known(context.funnelMetrics.roi),
      ],
      'Investment, revenue, CAC and ROI inputs available.',
    ),
    dimension(
      'goals',
      operational.map((node) => known(node.metrics.goal)).concat(context.target ? [true] : []),
      'Node goals or reverse-planning target available.',
    ),
    dimension(
      'retention',
      retention.length
        ? retention.flatMap((node) => [known(node.metrics.entries), known(node.metrics.exits)])
        : [],
      'Retention stages and volume inputs available.',
    ),
  ];
  return {
    overall: dimensions.reduce((sum, item) => sum + item.coverage, 0) / dimensions.length,
    dimensions,
  };
}

export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence < 0.4) return 'low';
  if (confidence < 0.75) return 'medium';
  return 'high';
}

export const CONFIDENCE_COMPONENT_WEIGHTS = {
  completeness: 0.2,
  evidenceQuality: 0.2,
  originQuality: 0.2,
  ruleDeterminism: 0.2,
  topologyClarity: 0.2,
} as const;

export function confidenceComponents(
  evidence: readonly DiagnosisEvidence[],
  coverage: number,
  topologyAmbiguous = false,
): DiagnosisConfidenceComponents {
  const originTrust: Record<DiagnosisEvidence['source'], number> = {
    manual: 1,
    calculated: 1,
    graph: 1,
    scenario: 1,
    rule: 1,
    benchmark: 0.75,
    imported: 0.6,
    ai: 0.25,
  };
  return {
    completeness: coverage,
    evidenceQuality: Math.min(1, evidence.length / 3),
    originQuality: evidence.length
      ? evidence.reduce((sum, item) => sum + originTrust[item.source], 0) / evidence.length
      : 0,
    ruleDeterminism: 1,
    topologyClarity: topologyAmbiguous ? 0.5 : 1,
  };
}

export function evidenceConfidence(components: DiagnosisConfidenceComponents): number {
  return Math.max(
    0,
    Math.min(
      1,
      Object.entries(CONFIDENCE_COMPONENT_WEIGHTS).reduce(
        (sum, [key, weight]) =>
          sum + components[key as keyof DiagnosisConfidenceComponents] * weight,
        0,
      ),
    ),
  );
}
